import { clientWithApiClient } from "../../client";
import type { ApiClient } from "../../types";

import type { ListResponse, Response } from "../../types";
import type {
  Addon,
  AssignAddonRequest,
  CreateAddonRequest,
  UpdateAddonRequest,
  AddonFilters,
  AddonScopeItem,
  AddonListMeta,
  BulkAddonConditionsRequest,
  BulkAddonConditionsResponse,
  VehicleAddon,
} from "./types";

/**
 * Retrieves a paginated list of addons from a specific fleet
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the addons
 * @param filters - Optional filters including pagination
 * @returns Promise that resolves to a paginated list of addons
 *
 * @example
 * ```typescript
 * const { data, meta } = await addons.list(apiClient, "fleet-123", { page: 1, per_page: 20 });
 * ```
 */
const list = (
  apiClient: ApiClient,
  fleetId: string,
  filters?: AddonFilters,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  if (filters?.page) searchParams.append("page", filters.page.toString());
  if (filters?.per_page)
    searchParams.append("per_page", filters.per_page.toString());
  if (filters?.active !== undefined)
    searchParams.append("active", filters.active.toString());

  const queryString = searchParams.toString();
  const url = queryString ? `/addons?${queryString}` : "/addons";

  return clientWithApiClient<ListResponse<Addon> & { meta?: AddonListMeta }>(
    apiClient,
    url,
  );
};

/**
 * Retrieves a specific addon by its ID within a fleet
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the addon
 * @param id - Unique identifier of the addon
 * @returns Promise that resolves to the requested addon
 *
 * @example
 * ```typescript
 * const { data: addon } = await addons.get(apiClient, "fleet-123", "addon-456");
 * ```
 */
const get = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString ? `/addons/${id}?${queryString}` : `/addons/${id}`;

  return clientWithApiClient<Response<Addon>>(apiClient, url);
};

/**
 * Creates a new addon in the specified fleet
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID where the addon will be created
 * @param addon - Addon data to create
 * @returns Promise that resolves to the created addon
 *
 * @example
 * ```typescript
 * const { data: newAddon } = await addons.create(apiClient, "fleet-123", {
 *   name: "GPS Navigation",
 *   price: 5.00,
 *   unit: "per_day"
 * });
 * ```
 */
const create = (
  apiClient: ApiClient,
  fleetId: string,
  addon: CreateAddonRequest,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString ? `/addons?${queryString}` : "/addons";

  return clientWithApiClient<Response<Addon>>(apiClient, url, {
    method: "POST",
    body: JSON.stringify({ addon }),
  });
};

/**
 * Updates an existing addon
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the addon
 * @param id - Unique identifier of the addon
 * @param addon - Updated addon data
 * @returns Promise that resolves to the updated addon
 *
 * @example
 * ```typescript
 * const { data: updatedAddon } = await addons.update(apiClient, "fleet-123", "addon-456", {
 *   price: 7.50
 * });
 * ```
 */
const update = (
  apiClient: ApiClient,
  fleetId: string,
  id: string,
  addon: UpdateAddonRequest,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString ? `/addons/${id}?${queryString}` : `/addons/${id}`;

  return clientWithApiClient<Response<Addon>>(apiClient, url, {
    method: "PATCH",
    body: JSON.stringify({ addon }),
  });
};

/**
 * Deletes an addon from the fleet
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the addon
 * @param id - Unique identifier of the addon
 * @returns Promise that resolves when the addon has been deleted
 *
 * @example
 * ```typescript
 * await addons.delete(apiClient, "fleet-123", "addon-456");
 * ```
 */
const remove = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString ? `/addons/${id}?${queryString}` : `/addons/${id}`;

  return clientWithApiClient(apiClient, url, {
    method: "DELETE",
  });
};

/**
 * Restores a deleted addon, with its vehicle and category assignments and its
 * old position in the list
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the addon
 * @param id - Unique identifier of the deleted addon
 * @returns Promise that resolves to the restored addon
 *
 * @example
 * ```typescript
 * const { data: addon } = await addons.restore(apiClient, "fleet-123", "addon-456");
 * ```
 */
const restore = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  return clientWithApiClient<Response<Addon>>(
    apiClient,
    `/addons/${id}/restore?${searchParams.toString()}`,
    { method: "POST" },
  );
};

/**
 * Retrieves all addons assigned to a specific vehicle
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the vehicle
 * @param vehicleId - Unique identifier of the vehicle
 * @returns Promise that resolves to a list of addons
 *
 * @example
 * ```typescript
 * const { data: addons } = await addons.listVehicleAddons(apiClient, "fleet-123", "vehicle-456");
 * ```
 */
const listVehicleAddons = (
  apiClient: ApiClient,
  fleetId: string,
  vehicleId: string,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/vehicles/${vehicleId}/addons?${queryString}`
    : `/vehicles/${vehicleId}/addons`;

  return clientWithApiClient<ListResponse<VehicleAddon>>(apiClient, url);
};

/**
 * Retrieves every addon of the fleet with whether a vehicle offers it and why
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the vehicle
 * @param vehicleId - Unique identifier of the vehicle
 * @returns Promise that resolves to the fleet's addons with `reason` and `offered`
 *
 * @example
 * ```typescript
 * const { data } = await addons.vehicleScope(apiClient, "fleet-123", "vehicle-456");
 * ```
 */
const vehicleScope = (
  apiClient: ApiClient,
  fleetId: string,
  vehicleId: string,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  return clientWithApiClient<ListResponse<AddonScopeItem>>(
    apiClient,
    `/vehicles/${encodeURIComponent(vehicleId)}/addon-scope?${searchParams.toString()}`,
  );
};

/**
 * Assigns an addon to a specific vehicle
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the vehicle
 * @param vehicleId - Unique identifier of the vehicle
 * @param request - Addon assignment data with addon_id
 * @returns Promise that resolves when the addon has been assigned
 *
 * @example
 * ```typescript
 * await addons.assignAddonToVehicle(apiClient, "fleet-123", "vehicle-456", { addon_id: "addon-789" });
 * ```
 */
const assignAddonToVehicle = (
  apiClient: ApiClient,
  fleetId: string,
  vehicleId: string,
  request: AssignAddonRequest,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/vehicles/${vehicleId}/addons?${queryString}`
    : `/vehicles/${vehicleId}/addons`;

  return clientWithApiClient<Response<{ message: string }>>(apiClient, url, {
    method: "POST",
    body: JSON.stringify(request),
  });
};

/**
 * Removes an addon from a specific vehicle
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the vehicle
 * @param vehicleId - Unique identifier of the vehicle
 * @param addonId - Unique identifier of the addon to remove
 * @returns Promise that resolves when the addon has been removed
 *
 * @example
 * ```typescript
 * await addons.removeAddonFromVehicle(apiClient, "fleet-123", "vehicle-456", "addon-789");
 * ```
 */
const removeAddonFromVehicle = (
  apiClient: ApiClient,
  fleetId: string,
  vehicleId: string,
  addonId: string,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/vehicles/${vehicleId}/addons/${addonId}?${queryString}`
    : `/vehicles/${vehicleId}/addons/${addonId}`;

  return clientWithApiClient(apiClient, url, {
    method: "DELETE",
  });
};

/**
 * Retrieves all addons assigned to a specific category
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the category
 * @param categoryId - Unique identifier of the category
 * @returns Promise that resolves to a list of addons
 *
 * @example
 * ```typescript
 * const { data: addons } = await addons.listCategoryAddons(apiClient, "fleet-123", "category-456");
 * ```
 */
const listCategoryAddons = (
  apiClient: ApiClient,
  fleetId: string,
  categoryId: string,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/categories/${categoryId}/addons?${queryString}`
    : `/categories/${categoryId}/addons`;

  return clientWithApiClient<ListResponse<Addon>>(apiClient, url);
};

/**
 * Assigns an addon to a specific category
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the category
 * @param categoryId - Unique identifier of the category
 * @param request - Addon assignment data with addon_id
 * @returns Promise that resolves when the addon has been assigned
 *
 * @example
 * ```typescript
 * await addons.assignAddonToCategory(apiClient, "fleet-123", "category-456", { addon_id: "addon-789" });
 * ```
 */
const assignAddonToCategory = (
  apiClient: ApiClient,
  fleetId: string,
  categoryId: string,
  request: AssignAddonRequest,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/categories/${categoryId}/addons?${queryString}`
    : `/categories/${categoryId}/addons`;

  return clientWithApiClient<Response<{ message: string }>>(apiClient, url, {
    method: "POST",
    body: JSON.stringify(request),
  });
};

/**
 * Removes an addon from a specific category
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the category
 * @param categoryId - Unique identifier of the category
 * @param addonId - Unique identifier of the addon to remove
 * @returns Promise that resolves when the addon has been removed
 *
 * @example
 * ```typescript
 * await addons.removeAddonFromCategory(apiClient, "fleet-123", "category-456", "addon-789");
 * ```
 */
const removeAddonFromCategory = (
  apiClient: ApiClient,
  fleetId: string,
  categoryId: string,
  addonId: string,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/categories/${categoryId}/addons/${addonId}?${queryString}`
    : `/categories/${categoryId}/addons/${addonId}`;

  return clientWithApiClient(apiClient, url, {
    method: "DELETE",
  });
};

/**
 * Sets, or with `conditions: null` clears, the conditions of many add-ons in
 * one call. All or nothing: any refusal means no add-on changed. The server
 * takes at most `ADDONS_BULK_CONDITIONS_MAX` ids and replaces each add-on's
 * conditions (it never merges); `required` and the scope stay as saved.
 *
 * @example
 * ```typescript
 * const { meta } = await addons.bulkConditions(apiClient, "fleet-123", {
 *   addon_ids: ["addon-1", "addon-2"],
 *   conditions: null,
 * });
 * ```
 */
const bulkConditions = (
  apiClient: ApiClient,
  fleetId: string,
  request: BulkAddonConditionsRequest,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  return clientWithApiClient<BulkAddonConditionsResponse>(
    apiClient,
    `/addons/bulk-conditions?${searchParams.toString()}`,
    {
      method: "POST",
      // The `conditions` key is always present: the server refuses a body
      // without it so a forgotten key can never wipe conditions.
      body: JSON.stringify({
        addon_ids: request.addon_ids,
        conditions: request.conditions,
      }),
    },
  );
};

/**
 * Addons API for fleet addon management
 *
 * Provides complete CRUD operations for addon management, plus operations for
 * managing addons on vehicles and categories.
 * All operations are scoped by fleetId to ensure proper resource isolation and access control.
 */
export const addons = {
  // Main CRUD operations
  list,
  get,
  create,
  update,
  delete: remove,
  restore,
  bulkConditions,

  // Vehicle addons
  listVehicleAddons,
  vehicleScope,
  assignAddonToVehicle,
  removeAddonFromVehicle,

  // Category addons
  listCategoryAddons,
  assignAddonToCategory,
  removeAddonFromCategory,
};
